/**
 * Хук загрузки interaction events для board/table секций.
 * Дата: 17.03.2026
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { interactionsApi } from '@/api/interactions';
import type { InteractionEvent } from '@/types/interaction';
import { getInteractionSectionConfig, type InteractionSectionId } from '@/features/interactions/registry/sectionRegistry';

const TABLE_PAGE_SIZE = 30;
const BOARD_LIMIT = 200;

// 17.03.2026 - Хук скрывает различие между board/table секциями и их API-настройками.
export function useInteractionEvents(params: {
  tenantId?: string;
  sectionId: InteractionSectionId;
  search: string;
  page: number;
  dateFrom?: Date | null;
  dateTo?: Date | null;
}) {
  const { tenantId, sectionId, search, page, dateFrom, dateTo } = params;
  const section = getInteractionSectionConfig(sectionId);

  const query = useQuery({
    queryKey: [
      'interactions-events',
      tenantId,
      sectionId,
      search,
      page,
      dateFrom?.toISOString(),
      dateTo?.toISOString(),
      section.scenarioKind,
    ],
    queryFn: async () => {
      // 15.04.26: dashboard пока управляет только tenant/date/search/scenario/eventType/hasAppointment.
      // Поля channel/status в InteractionFilters зарезервированы для будущих фильтров и здесь не отправляются.
      const tableEventType = section.mode === 'table' && section.eventTypes?.length === 1 ? section.eventTypes[0] : undefined;
      const response = await interactionsApi.list({
        tenant_id: tenantId as string,
        date_from: dateFrom?.toISOString(),
        date_to: dateTo?.toISOString(),
        search: search || undefined,
        scenario_kind: tableEventType ? undefined : section.scenarioKind || undefined,
        has_appointment: section.mode === 'board' ? section.hasAppointment : undefined,
        event_type: tableEventType,
        limit: section.mode === 'board' ? BOARD_LIMIT : TABLE_PAGE_SIZE,
        offset: section.mode === 'board' ? 0 : page * TABLE_PAGE_SIZE,
      });

      if (section.mode === 'board' && section.eventTypes?.length) {
        const filtered = (response.events || []).filter((event) => section.eventTypes?.includes(event.event_type));
        return {
          events: filtered,
          total: filtered.length,
          limit: BOARD_LIMIT,
          offset: 0,
        };
      }

      return response;
    },
    enabled: !!tenantId,
  });

  const kanbanColumns = useMemo(() => {
    const columns: Record<string, InteractionEvent[]> = {};
    (section.eventTypes || []).forEach((type) => {
      columns[type] = [];
    });

    (query.data?.events || []).forEach((event) => {
      if (columns[event.event_type]) {
        columns[event.event_type].push(event);
      }
    });

    return columns;
  }, [query.data?.events, section.eventTypes]);

  return {
    events: query.data?.events || [],
    total: query.data?.total || 0,
    pageSize: section.mode === 'board' ? BOARD_LIMIT : TABLE_PAGE_SIZE,
    kanbanColumns,
    isLoading: query.isLoading,
  };
}
